import { Edit, Package, Plus, Save, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import ModelSelector from '../components/ModelSelector';
import API_BASE_URL, { API_ENDPOINTS, buildApiUrl } from '../config/api.js';
import { useAuth } from '../contexts/AuthContext';
import './Accesorios.css';

const Accesorios = () => {
  const { authenticatedFetch } = useAuth();
  const [accessories, setAccessories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAccessory, setSelectedAccessory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    imageUrl: '',
    accessoryPriceType: 'FIXED',
    price: '',
    category: 'HELMET'
  });
  const [translations, setTranslations] = useState({
    es: { name: '', description: '' },
    en: { name: '', description: '' },
    de: { name: '', description: '' }
  });
  const [selectedModels, setSelectedModels] = useState([]);



  const categories = [
    { value: 'HELMET', label: 'Casco' },
    { value: 'LOCK', label: 'Candado' },
    { value: 'GLOVES', label: 'Guantes' },
    { value: 'BACKPACK', label: 'Mochila' },
    { value: 'GPS', label: 'GPS' },
    { value: 'PEDAL', label: 'Pedal' },
    { value: 'INSURANCE', label: 'Seguro' },
    { value: 'OTHER', label: 'Otro' }
  ];

  const priceTypes = [
    { value: 'FIXED', label: 'Precio fijo' },
    { value: 'DAILY', label: 'Precio diario' }
  ];

  // Cargar accesorios
  const loadAccessories = async () => {
    try {
      setLoading(true);
      // Cargar sin filtro de idioma para el admin (mostrar todos los datos)
      // El backend devuelve las traducciones en el campo translations
      const response = await authenticatedFetch(buildApiUrl(API_ENDPOINTS.ACCESSORIES));
      
      if (response.ok) {
        const data = await response.json();
        setAccessories(data);
      } else {
        console.error('Error al cargar accesorios');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccessories();
  }, []);

  // Crear accesorio
  const createAccessory = async (e) => {
    e.preventDefault();
    try {
      // Validar que al menos hay traducciones
      const translationsArray = Object.entries(translations)
        .filter(([, data]) => data.name.trim() !== '')
        .map(([lang, data]) => ({
          language: lang,
          name: data.name.trim(),
          description: data.description.trim() || null
        }));
      
      if (translationsArray.length === 0) {
        alert('Debe proporcionar al menos un nombre en uno de los idiomas');
        return;
      }
      
      // Usar el primer nombre como fallback para el campo name (compatibilidad)
      const fallbackName = translationsArray[0].name;
      const fallbackDescription = translationsArray[0].description;
      
      const payload = {
        ...formData,
        name: fallbackName, // Para compatibilidad
        description: fallbackDescription, // Para compatibilidad
        translations: translationsArray
      };
      
      // Crear el accesorio
      const response = await authenticatedFetch(`${buildApiUrl(API_ENDPOINTS.ACCESSORIES)}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const newAccessory = await response.json();
        
        // Asociar el accesorio con los modelos seleccionados
        if (selectedModels.length > 0) {
          for (const model of selectedModels) {
            try {
              await authenticatedFetch(`${buildApiUrl(API_ENDPOINTS.ACCESSORIES)}/associate`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  accessoryId: newAccessory.id,
                  vehicleModelId: model.id
                })
              });
            } catch (error) {
              console.error(`Error asociando accesorio con modelo ${model.name}:`, error);
            }
          }
        }
        
        setShowCreateModal(false);
        resetForm();
        loadAccessories();
      } else {
        const error = await response.json();
        alert(`Error: ${error.message}`);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al crear el accesorio');
    }
  };

  // Actualizar accesorio
  const updateAccessory = async (e) => {
    e.preventDefault();
    try {
      // Validar que al menos hay traducciones
      const translationsArray = Object.entries(translations)
        .filter(([, data]) => data.name.trim() !== '')
        .map(([lang, data]) => ({
          language: lang,
          name: data.name.trim(),
          description: data.description.trim() || null
        }));
      
      if (translationsArray.length === 0) {
        alert('Debe proporcionar al menos un nombre en uno de los idiomas');
        return;
      }
      
      // Usar el primer nombre como fallback para el campo name (compatibilidad)
      const fallbackName = translationsArray[0].name;
      const fallbackDescription = translationsArray[0].description;
      
      const payload = {
        ...formData,
        name: fallbackName, // Para compatibilidad
        description: fallbackDescription, // Para compatibilidad
        translations: translationsArray
      };
      
      // Actualizar los datos del accesorio
      const response = await authenticatedFetch(buildApiUrl(`${API_ENDPOINTS.ACCESSORIES}/${selectedAccessory.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        // Obtener los modelos actualmente asociados
        const currentModels = selectedAccessory.compatibleModels?.map(model => model.vehicleModel) || [];
        const currentModelIds = currentModels.map(model => model.id);
        const newModelIds = selectedModels.map(model => model.id);
        
        // Encontrar modelos a agregar y eliminar
        const modelsToAdd = selectedModels.filter(model => !currentModelIds.includes(model.id));
        const modelsToRemove = currentModels.filter(model => !newModelIds.includes(model.id));
        
        // Agregar nuevas asociaciones
        for (const model of modelsToAdd) {
          try {
            await authenticatedFetch(`${buildApiUrl(API_ENDPOINTS.ACCESSORIES)}/associate`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                accessoryId: selectedAccessory.id,
                vehicleModelId: model.id
              })
            });
          } catch (error) {
            console.error(`Error asociando modelo ${model.name}:`, error);
          }
        }
        
        // Eliminar asociaciones existentes
        for (const model of modelsToRemove) {
          try {
            await authenticatedFetch(buildApiUrl(`${API_ENDPOINTS.ACCESSORIES}/${selectedAccessory.id}/model/${model.id}`), {
              method: 'DELETE',
              headers: {
                'Content-Type': 'application/json',
              }
            });
          } catch (error) {
            console.error(`Error desasociando modelo ${model.name}:`, error);
          }
        }
        
        setShowEditModal(false);
        resetForm();
        loadAccessories();
      } else {
        const error = await response.json();
        alert(`Error: ${error.message}`);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al actualizar el accesorio');
    }
  };

  // Eliminar accesorio
  const deleteAccessory = async (id) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este accesorio?')) {
      return;
    }

    try {
      const response = await authenticatedFetch(buildApiUrl(`${API_ENDPOINTS.ACCESSORIES}/${id}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        loadAccessories();
        alert('Accesorio eliminado correctamente');
      } else {
        const error = await response.json();
        
        if (error.error === 'IN_USE_ERROR') {
          alert('No se puede eliminar el accesorio porque está siendo usado en reservas activas.');
        } else {
          alert(`Error: ${error.message}`);
        }
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al eliminar el accesorio');
    }
  };

  // Editar accesorio
  const handleEdit = async (accessory) => {
    // Cargar el accesorio completo con traducciones
    try {
      const response = await authenticatedFetch(
        buildApiUrl(`${API_ENDPOINTS.ACCESSORIES}/${accessory.id}`)
      );
      
      if (response.ok) {
        const fullAccessory = await response.json();
        setSelectedAccessory(fullAccessory);
        
        setFormData({
          name: fullAccessory.name,
          description: fullAccessory.description || '',
          imageUrl: fullAccessory.imageUrl || '',
          accessoryPriceType: fullAccessory.accessoryPriceType,
          price: fullAccessory.price.toString(),
          category: fullAccessory.category
        });
        
        // Cargar traducciones si existen
        // Inicializar con valores vacíos para todos los idiomas
        const translationsData = {
          es: { name: '', description: '' },
          en: { name: '', description: '' },
          de: { name: '', description: '' }
        };
        
        // Si hay traducciones, cargarlas
        if (fullAccessory.translations && fullAccessory.translations.length > 0) {
          console.log('Traducciones encontradas:', fullAccessory.translations);
          fullAccessory.translations.forEach(trans => {
            if (translationsData[trans.language]) {
              translationsData[trans.language] = {
                name: trans.name || '',
                description: trans.description !== undefined && trans.description !== null 
                  ? trans.description 
                  : ''
              };
            }
          });
        }
        
        // Si no hay traducción para español, usar los valores por defecto del accesorio
        if (!translationsData.es.name && fullAccessory.name) {
          translationsData.es.name = fullAccessory.name;
        }
        if (!translationsData.es.description && fullAccessory.description) {
          translationsData.es.description = fullAccessory.description;
        }
        
        console.log('Traducciones a mostrar:', translationsData);
        setTranslations(translationsData);
        
        // Cargar modelos compatibles del accesorio (solo con id para que el selector los muestre)
        const compatibleModels = (fullAccessory.compatibleModels || [])
          .map(cm => cm.vehicleModel)
          .filter(m => m && m.id);
        setSelectedModels(compatibleModels);
        
        setShowEditModal(true);
      } else {
        // Si falla, usar el accesorio que se pasó como parámetro
        console.warn('No se pudo cargar el accesorio completo, usando datos de la lista');
        setSelectedAccessory(accessory);
        setFormData({
          name: accessory.name,
          description: accessory.description || '',
          imageUrl: accessory.imageUrl || '',
          accessoryPriceType: accessory.accessoryPriceType,
          price: accessory.price.toString(),
          category: accessory.category
        });
        // Intentar usar traducciones de la lista si existen, sino usar valores por defecto
        const translationsData = {
          es: { name: '', description: '' },
          en: { name: '', description: '' },
          de: { name: '', description: '' }
        };
        if (accessory.translations && accessory.translations.length > 0) {
          accessory.translations.forEach(trans => {
            if (translationsData[trans.language]) {
              translationsData[trans.language] = {
                name: trans.name || '',
                description: trans.description !== undefined && trans.description !== null 
                  ? trans.description 
                  : ''
              };
            }
          });
        }
        // Si no hay traducción para español, usar los valores por defecto del accesorio
        if (!translationsData.es.name && accessory.name) {
          translationsData.es.name = accessory.name;
        }
        if (!translationsData.es.description && accessory.description) {
          translationsData.es.description = accessory.description;
        }
        setTranslations(translationsData);
        const compatibleModels = (accessory.compatibleModels || [])
          .map(cm => cm.vehicleModel)
          .filter(m => m && m.id);
        setSelectedModels(compatibleModels);
        setShowEditModal(true);
      }
    } catch (error) {
      console.error('Error cargando detalles del accesorio:', error);
      // Si falla, usar el accesorio que se pasó como parámetro
      setSelectedAccessory(accessory);
      setFormData({
        name: accessory.name,
        description: accessory.description || '',
        imageUrl: accessory.imageUrl || '',
        accessoryPriceType: accessory.accessoryPriceType,
        price: accessory.price.toString(),
        category: accessory.category
      });
      // Intentar usar traducciones de la lista si existen, sino usar valores por defecto
      const translationsData = {
        es: { name: accessory.name || '', description: accessory.description || '' },
        en: { name: '', description: '' },
        de: { name: '', description: '' }
      };
      if (accessory.translations && accessory.translations.length > 0) {
        accessory.translations.forEach(trans => {
          if (translationsData[trans.language]) {
            translationsData[trans.language] = {
              name: trans.name || translationsData[trans.language].name || '',
              description: trans.description !== undefined && trans.description !== null 
                ? trans.description 
                : translationsData[trans.language].description || ''
            };
          }
        });
      }
      setTranslations(translationsData);
      const compatibleModels = (accessory.compatibleModels || [])
        .map(cm => cm.vehicleModel)
        .filter(m => m && m.id);
      setSelectedModels(compatibleModels);
      setShowEditModal(true);
    }
  };

  // Resetear formulario
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      imageUrl: '',
      accessoryPriceType: 'FIXED',
      price: '',
      category: 'HELMET'
    });
    setTranslations({
      es: { name: '', description: '' },
      en: { name: '', description: '' },
      de: { name: '', description: '' }
    });
    setSelectedAccessory(null);
    setSelectedModels([]);
  };

  // Filtrar accesorios
  const filteredAccessories = accessories.filter(accessory =>
    !filterCategory || accessory.category === filterCategory
  );

  // Obtener etiqueta de categoría
  const getCategoryLabel = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat ? cat.label : category;
  };

  // Obtener etiqueta de tipo de precio
  const getPriceTypeLabel = (priceType) => {
    const type = priceTypes.find(t => t.value === priceType);
    return type ? type.label : priceType;
  };

  if (loading) {
    return (
      <div className="accesorios-page">
        <div className="loading">Cargando accesorios...</div>
      </div>
    );
  }

  return (
    <div className="accesorios-page">
      <div className="page-header">
        <h1>Gestión de Accesorios</h1>
        <button 
          className="btn-primary"
          onClick={() => setShowCreateModal(true)}
        >
          <Plus size={16} />
          Nuevo Accesorio
        </button>
      </div>

      <div className="filters-section">
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="filter-select"
        >
          <option value="">Todas las categorías</option>
          {categories.map(cat => (
            <option key={cat.value} value={cat.value}>{cat.label}</option>
          ))}
        </select>
      </div>
      {filteredAccessories.length === 0 && (
        <div className="no-results">No se encontraron resultados</div>
      )}

      {filteredAccessories.length > 0 && (
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Nombre</th>
              <th>Descripción</th>
              <th>Categoría</th>
              <th>Tipo de Precio</th>
              <th>Precio</th>
              <th>Modelos Compatibles</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredAccessories.map((accessory) => (
              <tr key={accessory.id}>
                <td>#{accessory.id}</td>
                <td>{accessory.name}</td>
                <td>{accessory.description || '-'}</td>
                <td>
                  <span className="category-badge">
                    {getCategoryLabel(accessory.category)}
                  </span>
                </td>
                <td>{getPriceTypeLabel(accessory.accessoryPriceType)}</td>
                <td>€{accessory.price}</td>
                <td>
                  <div className="compatible-models-cell" title={accessory.compatibleModels?.length
                    ? accessory.compatibleModels.map(m => m.vehicleModel?.name).filter(Boolean).join(', ')
                    : ''
                  }>
                    {accessory.compatibleModels && accessory.compatibleModels.length > 0 ? (
                      <span className="models-summary">
                        {accessory.compatibleModels.length === 1
                          ? `1 modelo: ${accessory.compatibleModels[0].vehicleModel?.name ?? ''}`
                          : `${accessory.compatibleModels.length} modelos`
                        }
                      </span>
                    ) : (
                      <span className="no-models">Sin modelos asignados</span>
                    )}
                  </div>
                </td>
                <td>
                  <div className="action-buttons">
                    <button 
                      className="btn-icon" 
                      title="Editar"
                      onClick={() => handleEdit(accessory)}
                    >
                      <Edit size={16} />
                    </button>
                    <button 
                      className="btn-icon btn-danger" 
                      title="Eliminar"
                      onClick={() => deleteAccessory(accessory.id)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal para crear accesorio */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Nuevo Accesorio</h2>
              <button 
                className="btn-icon"
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={createAccessory} className="modal-form">
              <div className="form-group">
                <label>URL de Imagen</label>
                <input
                  type="url"
                  value={formData.imageUrl}
                  onChange={(e) => setFormData({...formData, imageUrl: e.target.value})}
                  placeholder="https://ejemplo.com/imagen.jpg"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Categoría *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    required
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Tipo de Precio *</label>
                  <select
                    value={formData.accessoryPriceType}
                    onChange={(e) => setFormData({...formData, accessoryPriceType: e.target.value})}
                    required
                  >
                    {priceTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Precio *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({...formData, price: e.target.value})}
                  required
                />
              </div>
              
              {/* Sección de Traducciones */}
              <div className="form-group">
                <label style={{ marginBottom: '1rem', fontWeight: 'bold', fontSize: '1.1rem' }}>
                  Traducciones (Multiidioma) *
                </label>
                <div style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '1rem', backgroundColor: '#f9f9f9' }}>
                  {['es', 'en', 'de'].map((lang) => (
                    <div key={lang} style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: lang !== 'de' ? '1px solid #eee' : 'none' }}>
                      <h4 style={{ marginBottom: '0.5rem', color: '#333' }}>
                        {lang === 'es' ? 'Español' : lang === 'en' ? 'English' : 'Deutsch'} *
                      </h4>
                      <div className="form-group" style={{ marginBottom: '0.5rem' }}>
                        <label>Nombre *</label>
                        <input
                          type="text"
                          value={translations[lang].name}
                          onChange={(e) => setTranslations({
                            ...translations,
                            [lang]: { ...translations[lang], name: e.target.value }
                          })}
                          required
                          placeholder={`Nombre en ${lang === 'es' ? 'español' : lang === 'en' ? 'inglés' : 'alemán'}`}
                        />
                      </div>
                      <div className="form-group">
                        <label>Descripción</label>
                        <textarea
                          value={translations[lang].description}
                          onChange={(e) => setTranslations({
                            ...translations,
                            [lang]: { ...translations[lang], description: e.target.value }
                          })}
                          rows="2"
                          placeholder={`Descripción en ${lang === 'es' ? 'español' : lang === 'en' ? 'inglés' : 'alemán'}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <ModelSelector
                selectedModels={selectedModels}
                onSelectionChange={setSelectedModels}
              />
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  <Save size={16} />
                  Crear
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal para editar accesorio */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Editar Accesorio</h2>
              <button 
                className="btn-icon"
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={updateAccessory} className="modal-form">
              <div className="form-group">
                <label>URL de Imagen</label>
                <input
                  type="url"
                  value={formData.imageUrl}
                  onChange={(e) => setFormData({...formData, imageUrl: e.target.value})}
                  placeholder="https://ejemplo.com/imagen.jpg"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Categoría *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    required
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Tipo de Precio *</label>
                  <select
                    value={formData.accessoryPriceType}
                    onChange={(e) => setFormData({...formData, accessoryPriceType: e.target.value})}
                    required
                  >
                    {priceTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Precio *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({...formData, price: e.target.value})}
                  required
                />
              </div>
              
              {/* Sección de Traducciones */}
              <div className="form-group">
                <label style={{ marginBottom: '1rem', fontWeight: 'bold', fontSize: '1.1rem' }}>
                  Traducciones (Multiidioma)
                </label>
                <div style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '1rem', backgroundColor: '#f9f9f9' }}>
                  {['es', 'en', 'de'].map((lang) => (
                    <div key={lang} style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: lang !== 'de' ? '1px solid #eee' : 'none' }}>
                      <h4 style={{ marginBottom: '0.5rem', color: '#333' }}>
                        {lang === 'es' ? 'Español' : lang === 'en' ? 'English' : 'Deutsch'} *
                      </h4>
                      <div className="form-group" style={{ marginBottom: '0.5rem' }}>
                        <label>Nombre *</label>
                        <input
                          type="text"
                          value={translations[lang].name}
                          onChange={(e) => setTranslations({
                            ...translations,
                            [lang]: { ...translations[lang], name: e.target.value }
                          })}
                          required
                          placeholder={`Nombre en ${lang === 'es' ? 'español' : lang === 'en' ? 'inglés' : 'alemán'}`}
                        />
                      </div>
                      <div className="form-group">
                        <label>Descripción</label>
                        <textarea
                          value={translations[lang].description}
                          onChange={(e) => setTranslations({
                            ...translations,
                            [lang]: { ...translations[lang], description: e.target.value }
                          })}
                          rows="2"
                          placeholder={`Descripción en ${lang === 'es' ? 'español' : lang === 'en' ? 'inglés' : 'alemán'}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <ModelSelector
                selectedModels={selectedModels}
                onSelectionChange={setSelectedModels}
              />
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  <Save size={16} />
                  Guardar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Accesorios; 